import { CalendarClock, CheckCircle2, Info, LucideIconData, Minus, Plus, X } from 'lucide-angular';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  PrepurchaseDeliveryMethod,
  PrepurchaseError,
  PrepurchaseErrorReason,
  PrepurchasePaymentMethod,
  PrepurchaseRequest,
  PrepurchaseSession,
  PrepurchaseSessionRequest,
} from '../../models/prepurchase.model';
import { filter, fromEvent, take } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  CHECKOUT_COPY,
  CheckoutPaymentMethod,
  PREPURCHASE_COPY,
  PS_DELIVERY_COPY,
  withMonto,
} from '../../../features/checkout/checkout-copy';
import {
  isPsDeliveryCovered,
  isPsDeliveryProvince,
  psDeliveryPriceFor,
} from '../../services/ps-delivery-coverage';

import { AuthModalService } from '../../services/auth-modal.service';
import { AuthService } from '../../services/auth.service';
import { PointsService } from '../../services/points.service';
import {
  PERRON_POINTS_COPY,
  vipBadgeText,
  withEarlyAccessInsufficient,
  withThreshold,
} from '../../perron-points/perron-points-copy';
import { BadgeComponent } from '../badge/badge.component';
import { ButtonComponent } from '../button/button.component';
import { PpCoinComponent } from '../pp-coin/pp-coin.component';
import { CountdownComponent } from '../countdown/countdown.component';
import { CoverageNoticeDialogComponent } from '../coverage-notice-dialog/coverage-notice-dialog.component';
import { PsDeliveryDestinationsDialogComponent } from '../ps-delivery-destinations-dialog/ps-delivery-destinations-dialog.component';
import { DeliveryService } from '../../services/delivery.service';
import { LocationsService } from '../../services/locations.service';
import { IconComponent } from '../icon/icon.component';
import {
  PaymentDataCopyOverrides,
  PaymentDataModalComponent,
} from '../../../features/checkout/components/payment-data-modal/payment-data-modal.component';
import { PaymentService } from '../../services/payment.service';
import { PrepurchaseModalService } from '../../services/prepurchase-modal.service';
import { PrepurchaseService } from '../../services/prepurchase.service';
import { isEarlyAccessProduct as computeIsEarlyAccessProduct, Product } from '../../models/product.model';
import {
  earlyAccessThresholdOf,
  evaluateEarlyAccess,
} from '../../perron-points/early-access-gate';
import { Router } from '@angular/router';
import { ToastService } from '../../services/toast.service';
import { User } from '../../models/user.model';
import { isPlatformBrowser } from '@angular/common';
import { scrollBehavior } from '../../utils/scroll';
import { formatCrc } from '../../utils/currency.util';

const FALLBACK_IMAGE_URL = '/images/hero/broken_hero.jpg';

/** Clave localStorage con datos de contacto del usuario para prellenar el form. */
const CONTACT_CACHE_KEY = 'perron.prepurchase.contact';

/** Abono inicial que muestra el backend (30%). Si el backend cambia este porcentaje, actualizar. */
const DEPOSIT_PERCENT = 0.3;

interface CachedContact {
  name?: string;
  surnames?: string;
  identification?: string;
  email?: string;
  phone?: string;
  province?: string;
  canton?: string;
  district?: string;
  hood?: string;
  directions?: string;
}

type ErrorPanel = { reason: PrepurchaseErrorReason; message: string } | null;

const dateFormatter = new Intl.DateTimeFormat('es-CR', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

const shortDateFormatter = new Intl.DateTimeFormat('es-CR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const ERROR_WORDING: Record<PrepurchaseErrorReason, string> = {
  limit_exceeded: 'Superaste el máximo de unidades permitidas.',
  stock_exhausted: 'Ya no quedan suficientes unidades para reservar esta cantidad.',
  duplicate_monthly: 'Ya tienes una reserva de este producto en el último mes.',
  // Rótulo VERBATIM del 409 (cupo derivado agotado, Bloque 2). Fuente única: copy centralizado.
  sold_out: PREPURCHASE_COPY.reservation.soldOut,
  invalid_session: PREPURCHASE_COPY.reservation.invalidSession,
  // Rótulo VERBATIM del 429 (rate-limiting de la apertura de reserva). Mismo trato que sold_out.
  too_many_requests: PREPURCHASE_COPY.reservation.throttled,
  validation: 'Revisa los datos del formulario.',
  network: 'Error de conexión. Inténtalo de nuevo.',
  // Fallback estático: `errorHeading()` los sustituye por `panel.message` (ya
  // interpolado con el umbral/saldo reales) antes de llegar a pintarse.
  ea_login_required: PERRON_POINTS_COPY.earlyAccess.guestLockedFallback,
  ea_insufficient_points: PERRON_POINTS_COPY.earlyAccess.insufficientLockedFallback,
  unknown: 'No pudimos procesar tu reserva. Inténtalo de nuevo.',
};

@Component({
  selector: 'app-prepurchase-modal',
  imports: [
    ReactiveFormsModule,
    IconComponent,
    ButtonComponent,
    BadgeComponent,
    CountdownComponent,
    CoverageNoticeDialogComponent,
    PsDeliveryDestinationsDialogComponent,
    PaymentDataModalComponent,
    PpCoinComponent,
  ],
  templateUrl: './prepurchase-modal.component.html',
  styleUrl: './prepurchase-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrepurchaseModalComponent {
  private readonly modalService = inject(PrepurchaseModalService);
  private readonly prepurchaseService = inject(PrepurchaseService);
  private readonly paymentService = inject(PaymentService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly authModalService = inject(AuthModalService);
  private readonly deliveryService = inject(DeliveryService);
  private readonly locationsService = inject(LocationsService);
  protected readonly pointsService = inject(PointsService);

  protected readonly isOpen = this.modalService.isOpen;
  protected readonly product = this.modalService.product;
  protected readonly isAuthenticated = this.authService.isAuthenticated;
  protected readonly currentUser = this.authService.currentUser;
  protected readonly delivery1 = this.deliveryService.delivery1;

  /** Copy de Early Access (Perrón Points, Web F2). */
  protected readonly eaCopy = PERRON_POINTS_COPY.earlyAccess;
  protected readonly withThreshold = withThreshold;
  protected readonly withEarlyAccessInsufficient = withEarlyAccessInsufficient;

  /** Mismo criterio que el badge (card/zoom/ficha): `isEarlyAccessProduct` (R7). */
  protected readonly isEarlyAccessProduct = computed(() =>
    computeIsEarlyAccessProduct(this.product()),
  );

  /**
   * Umbral EFECTIVO de esta precompra: el resuelto por el servidor para ESTE
   * producto (`early_access_threshold`, R8) y, solo si el payload no lo trae,
   * el último umbral de usuario conocido o el fallback. Antes se usaba el
   * umbral global de usuario, que con umbrales por producto (R5) dejaría pasar
   * al formulario a quien el servidor va a rechazar.
   */
  protected readonly eaThreshold = computed(
    () =>
      earlyAccessThresholdOf(this.product(), this.pointsService.displayThreshold())
      ?? this.pointsService.displayThreshold(),
  );

  /** Badge de identidad VIP, con la variante de umbral 0. */
  protected readonly eaBadgeText = computed(() => vipBadgeText(this.eaThreshold()));

  /**
   * Gate client-side (UX primaria, evita rellenar el formulario para nada). El
   * servidor es el backstop real: los 3 endpoints de precompra re-validan el
   * mismo gate y sus 403 se mapean a las mismas 2 variantes (ver `errorPanel`).
   * Mientras el saldo no esté resuelto, el gate permanece cerrado (`loading`):
   * un fallo o una respuesta lenta de `/perron-points/me` nunca desbloquea la
   * compra por defecto. Única excepción, la de R5: con umbral `0` basta la
   * sesión y el saldo no se mira.
   */
  protected readonly eaVariant = computed<
    'none' | 'login' | 'loading' | 'error' | 'insufficient' | 'granted'
  >(() => {
    if (!this.isEarlyAccessProduct()) return 'none';

    const decision = evaluateEarlyAccess(
      earlyAccessThresholdOf(this.product(), this.pointsService.displayThreshold()),
      {
        authenticated: this.isAuthenticated(),
        balance: this.pointsService.balance(),
        // `threshold()` no nulo ⟺ `/perron-points/me` ya resolvió (es
        // `summary()?.threshold`), que es cuando `balance()` es de fiar.
        balanceKnown: this.pointsService.threshold() !== null,
        balanceError: this.pointsService.loadError(),
      },
    );

    if (decision.allowed) return 'granted';
    return decision.reason === 'guest' ? 'login' : decision.reason;
  });

  protected readonly eaLocked = computed(
    () => this.eaVariant() !== 'none' && this.eaVariant() !== 'granted',
  );

  protected onRetryPoints(): void {
    this.pointsService.refresh();
  }

  /** Catálogo provincia→cantón→distrito (cascade selects, igual que el checkout). */
  protected readonly provinces = this.locationsService.provinces;
  protected readonly locationsLoadError = this.locationsService.loadError;

  protected readonly closeIcon: LucideIconData = X;
  protected readonly plusIcon: LucideIconData = Plus;
  protected readonly minusIcon: LucideIconData = Minus;
  protected readonly calendarIcon: LucideIconData = CalendarClock;
  protected readonly infoIcon: LucideIconData = Info;
  protected readonly successIcon: LucideIconData = CheckCircle2;

  protected readonly status = signal<'idle' | 'success'>('idle');
  protected readonly errorPanel = signal<ErrorPanel>(null);

  /**
   * Paso del flujo (Bloque 1): el submit del formulario YA NO crea la
   * precompra; pasa al paso de MÉTODO DE PAGO (sinpe/transfer) y la creación
   * ocurre al «Finalizar» del modal de comprobante (create-on-finalize).
   */
  protected readonly step = signal<'form' | 'payment'>('form');

  /** Copy centralizado de precompra (D14, verbatim con test). */
  protected readonly copy = PREPURCHASE_COPY;
  /** Encabezado del paso de pago: mismo del selector del 30 % del checkout. */
  protected readonly paymentStepTitle = CHECKOUT_COPY.layawayDepositSelector.title;
  /**
   * Opciones de pago del abono (reutilizan `CHECKOUT_COPY.paymentOptions`). La tarjeta (`online`,
   * Bloque 3) solo se ofrece cuando la pasarela real está activa (`providerId === 'onvo'`, D2): con el
   * stub no se enruta a la página de pago ONVO. SINPE/transferencia están siempre.
   */
  protected readonly paymentOptions =
    this.paymentService.providerId === 'onvo'
      ? CHECKOUT_COPY.paymentOptions
      : CHECKOUT_COPY.paymentOptions.filter((option) => option.key !== 'online');
  /**
   * Overrides de copy del modal de comprobante en contexto precompra. La línea
   * de Perrón Points se añade SOLO con sesión iniciada (la precompra de invitado
   * no acumula, D14/D19); así el checkout de carrito —otro uso del mismo
   * componente— nunca la recibe.
   */
  protected readonly paymentCopyOverrides = computed<PaymentDataCopyOverrides>(() => ({
    ...PREPURCHASE_COPY.paymentDataOverrides,
    ...(this.isAuthenticated() ? { perronPointsHint: PREPURCHASE_COPY.perronPoints.manual } : {}),
  }));

  /** Estado del modal de comprobante reutilizado (patrón checkout-page). */
  protected readonly paymentModalOpen = signal(false);
  /** El modal de comprobante solo cubre SINPE/transferencia; la tarjeta va por la página de pago ONVO. */
  protected readonly paymentModalMethod =
    signal<Exclude<PrepurchasePaymentMethod, 'online'>>('sinpe');
  protected readonly paymentModalSubmitting = signal(false);
  protected readonly paymentModalError = signal<string | null>(null);

  // ── Reserva flotante de 15 min (Bloque 2, D5) ────────────────────────────
  /** Sesión de reserva vigente. La abre «Continuar al pago»; el countdown corre contra su `expires_at`. */
  protected readonly session = signal<PrepurchaseSession | null>(null);
  /** Método que el usuario quiere usar; se conserva si renovar la sesión falla. */
  private readonly selectedPaymentMethod = signal<PrepurchasePaymentMethod>('sinpe');
  /** Método congelado en la sesión vigente; el API exige coherencia al consumirla. */
  private readonly sessionPaymentMethod = signal<PrepurchasePaymentMethod | null>(null);
  /** POST /prepurchase-sessions en vuelo: spinner en el CTA correspondiente. */
  protected readonly sessionOpening = signal(false);
  /**
   * Aviso a pantalla completa del paso de pago: la reserva expiró
   * (`expired`), el cupo se agotó al reintentar (`sold_out`) o el rate-limiter
   * cortó la apertura (`throttled`, 429). Todos ofrecen el CTA de reintento,
   * que abre una sesión NUEVA (nuevo countdown).
   */
  protected readonly reservationNotice = signal<'expired' | 'sold_out' | 'throttled' | null>(null);

  /** Payload validado en el submit del form, pendiente del método + comprobante. */
  private pendingPayload: Omit<PrepurchaseRequest, 'payment_method' | 'session_token'> | null =
    null;

  private readonly closeButtonRef = viewChild<ElementRef<HTMLButtonElement>>('closeBtn');
  private readonly modalRef = viewChild<ElementRef<HTMLElement>>('modalEl');
  private readonly bodyRef = viewChild<ElementRef<HTMLElement>>('bodyEl');
  private previouslyFocusedElement: HTMLElement | null = null;

  protected readonly form = this.fb.nonNullable.group({
    quantity: [1, [Validators.required, Validators.min(1)]],
    name: ['', [Validators.required, Validators.maxLength(100)]],
    surnames: ['', [Validators.required, Validators.maxLength(100)]],
    identification: ['', [Validators.maxLength(50)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(150)]],
    phone: ['', [Validators.required, Validators.maxLength(50)]],
    delivery_method: this.fb.nonNullable.control<PrepurchaseDeliveryMethod>('ps', [
      Validators.required,
    ]),
    province: ['', [Validators.maxLength(100)]],
    canton: ['', [Validators.maxLength(100)]],
    district: ['', [Validators.maxLength(100)]],
    hood: ['', [Validators.maxLength(150)]],
    directions: ['', [Validators.maxLength(300)]],
  });

  /** Espejo en signal del método elegido. Mantenido manualmente porque
   * `form.reset({...}, { emitEvent: false })` en `resetToProduct` no
   * dispara `valueChanges` y necesitamos mantener `requiresAddress()`
   * sincronizado con el valor real del control. */
  protected readonly deliveryMethod = signal<PrepurchaseDeliveryMethod>('ps');

  /**
   * Espejos en signal de provincia/cantón. Mantenidos a mano (como `deliveryMethod`) porque los
   * resets/patch silenciosos del form no emiten `valueChanges`; alimentan la cascada y la cobertura.
   */
  protected readonly provinceValue = signal('');
  protected readonly cantonValue = signal('');

  /**
   * «Mensajero de Perrón Store» (ps_delivery): tarifa server-side por provincia, cobertura limitada a
   * 19 cantones del GAM (ver ps-delivery-coverage). Mismo flag que en checkout-page.component.
   */
  protected readonly psDeliveryEnabled = true;

  protected readonly requiresAddress = computed(() => {
    const m = this.deliveryMethod();
    return m === 'home' || m === 'ps_delivery';
  });

  /** Cantones/distritos disponibles para la provincia/cantón elegidos (cascade). */
  protected readonly availableCantons = computed(() => {
    this.provinces();
    return this.locationsService.cantonsOf(this.provinceValue());
  });
  protected readonly availableDistricts = computed(() => {
    this.provinces();
    return this.locationsService.districtsOf(this.provinceValue(), this.cantonValue());
  });

  /** True si el par provincia+cantón actual está dentro de la cobertura del mensajero propio. */
  protected readonly psDeliveryCovered = computed(() =>
    isPsDeliveryCovered(this.provinceValue(), this.cantonValue()),
  );

  /**
   * Deshabilita la opción ps_delivery en el ORDEN INVERSO: el cliente está en «home» (único método
   * —además de ps_delivery— con dirección visible) y su cantón está fuera de cobertura. Si ya es
   * ps_delivery, el dialog reconduce a Correos (evita el estado contradictorio seleccionado+deshabilitado).
   */
  protected readonly psDeliveryOptionDisabled = computed(
    () => this.deliveryMethod() === 'home' && !!this.cantonValue() && !this.psDeliveryCovered(),
  );

  /** Dialog «Cambiamos tu método de envío» (cantón no cubierto con ps_delivery seleccionado). */
  protected readonly coverageDialogOpen = signal(false);
  /** Modal informativo «ver destinos disponibles» (provincias, tarifas y cantones). */
  protected readonly destinationsOpen = signal(false);
  protected readonly psDeliveryCopy = PS_DELIVERY_COPY;
  /** Tarifa del mensajero para la provincia elegida (o el mínimo, mostrado como «Desde»). */
  protected readonly psDeliveryPrice = computed(() => psDeliveryPriceFor(this.provinceValue()));
  /** True si la provincia elegida tiene tarifa propia → mostrar precio EXACTO en vez de «Desde». */
  protected readonly psDeliveryPriceExact = computed(() =>
    isPsDeliveryProvince(this.provinceValue()),
  );

  protected readonly deliveryMethodLabel = computed(() => {
    switch (this.deliveryMethod()) {
      case 'ps':
        return 'Retiro en Perrón Store';
      case 'uber':
        return 'Mensajero por tu cuenta';
      case 'home':
        return 'Correos de Costa Rica';
      case 'ps_delivery':
        return 'Mensajero de Perrón Store';
    }
  });

  /** Stock tope considerando `prepurchase_stock` y `prepurchase_limit`. 0 en `prepurchase_limit` significa "sin límite". */
  protected readonly maxQuantity = computed(() => {
    const p = this.product();
    if (!p) return 1;
    const stock = p.prepurchase_stock ?? 0;
    const perClient = p.prepurchase_limit ?? 0;
    if (perClient > 0) return Math.max(1, Math.min(stock, perClient));
    return Math.max(1, stock);
  });

  protected readonly unitPrice = computed(() => this.product()?.price ?? 0);

  protected readonly subtotal = computed(() => {
    const qty = this.form.controls.quantity.value ?? 1;
    return this.unitPrice() * qty;
  });

  /**
   * Costo de envío. Misma lógica que `computedDeliveryPrice` del checkout
   * pero leyendo el Delivery id=1 desde `DeliveryService` (el modal no
   * tiene cart context). `ps`/`uber` → 0; `ps_delivery` → tarifa por provincia
   * (el servidor es autoritativo y la re-deriva); sólo `home` aplica el threshold del backend.
   */
  protected readonly deliveryPrice = computed<number>(() => {
    const method = this.deliveryMethod();
    if (method === 'ps' || method === 'uber') return 0;
    if (method === 'ps_delivery') return psDeliveryPriceFor(this.provinceValue());

    const psd = this.delivery1();
    if (!psd) return 0;
    if (psd.free_delivery && this.subtotal() >= psd.min_for_free) return 0;
    return Number(psd.price);
  });

  /** Abono inicial (30%) sobre `subtotal + envío`. Coincide con el backend (LayawayPolicy). */
  protected readonly depositAmount = computed(() =>
    Math.round((this.subtotal() + this.deliveryPrice()) * DEPOSIT_PERCENT),
  );

  /**
   * Abono a MOSTRAR: con sesión abierta gana `amount_colones` (30 % calculado
   * SERVER-SIDE, autoritativo); sin sesión (paso de formulario) se enseña la
   * estimación cliente. Ambos coinciden salvo divergencia de redondeo.
   */
  protected readonly effectiveDeposit = computed(
    () => this.session()?.amount_colones ?? this.depositAmount(),
  );

  /** Saldo del 70 % restante (se paga al recibir el producto). */
  protected readonly remainingAmount = computed(
    () => this.subtotal() + this.deliveryPrice() - this.effectiveDeposit(),
  );

  /** Expiración absoluta de la reserva vigente (alimenta los countdowns). */
  protected readonly sessionExpiresAt = computed(() => this.session()?.expires_at ?? null);

  /**
   * El countdown se pinta en el paso de pago mientras la reserva siga viva y
   * no haya aviso de expiración/cupo en pantalla (D5: arranca al pasar al
   * paso de pago).
   */
  protected readonly showCountdown = computed(
    () =>
      this.status() === 'idle' &&
      this.step() === 'payment' &&
      this.session() !== null &&
      this.reservationNotice() === null,
  );

  /** Mensaje de éxito verbatim con el 30 % ya formateado en CRC. */
  protected readonly successMessage = computed(() =>
    withMonto(PREPURCHASE_COPY.success.message, this.formatCRC(this.effectiveDeposit())),
  );

  protected readonly isDeadlinePassed = computed(() => {
    const raw = this.product()?.prepurchase_due_date;
    if (!raw) return false;
    const due = new Date(raw);
    if (isNaN(due.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
  });

  protected readonly dueDateLabel = computed(() => {
    const raw = this.product()?.prepurchase_due_date;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : dateFormatter.format(d);
  });

  protected readonly dueDateShort = computed(() => {
    const raw = this.product()?.prepurchase_due_date;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : shortDateFormatter.format(d);
  });

  protected readonly perClientLimit = computed(() => {
    const n = this.product()?.prepurchase_limit ?? 0;
    return n > 0 ? n : null;
  });

  protected readonly thumbnailUrl = computed(() => this.product()?.imageUrl || FALLBACK_IMAGE_URL);

  constructor() {
    // Validators condicionales según método de envío. home/ps_delivery →
    // province, canton, district, directions REQUIRED. ps/uber → ningún
    // campo extra requerido y limpiamos los valores para no enviar basura
    // en el payload. Patrón análogo a checkout-page.component.ts:338-374.
    this.form.controls.delivery_method.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((method) => {
        this.applyDeliveryMethod(method, { resetAddress: true });
        this.reconcilePsDeliveryCoverage();
      });

    // Catálogo de ubicaciones para los cascade selects (no-op en SSR; cachea).
    this.locationsService.ensureLoaded();

    // ── Cascade provincia → cantón → distrito (mismo patrón que checkout) ──
    this.form.controls.province.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((province) => {
        this.provinceValue.set(province ?? '');
        this.form.patchValue({ canton: '', district: '' }, { emitEvent: false });
        this.cantonValue.set('');
      });

    this.form.controls.canton.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((canton) => {
        this.cantonValue.set(canton ?? '');
        this.form.patchValue({ district: '' }, { emitEvent: false });

        // Con selects, cada cambio ya representa un cantón completo. En el fallback de texto se
        // difiere la validación al blur para no rechazar valores parciales mientras el usuario escribe.
        if (!this.locationsLoadError()) this.reconcilePsDeliveryCoverage();
      });

    // Cada vez que el modal se abre: reset de estado, prellenar form, guardar foco anterior
    // y mover el foco al botón close. Se desencadena al cambiar el producto también.
    effect(() => {
      const open = this.isOpen();
      const product = this.product();
      if (!isPlatformBrowser(this.platformId)) return;

      if (open && product) {
        this.resetToProduct(product);
        // Hidratar Delivery id=1 lazy. El service cachea, así que aperturas
        // posteriores no refetchean. Si falla, dejamos `delivery1()` null:
        // `home` se bloquea al enviar; `ps_delivery` conserva su tarifa por provincia.
        this.deliveryService
          .loadDelivery1()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({ error: () => {} });
        this.previouslyFocusedElement = (document.activeElement as HTMLElement | null) ?? null;
        queueMicrotask(() => this.closeButtonRef()?.nativeElement.focus());
      } else if (!open && this.previouslyFocusedElement) {
        const el = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;
        queueMicrotask(() => {
          if (document.contains(el)) {
            el.focus({ preventScroll: true });
          }
        });
      }
    });

    // Captura el primer login de la sesión para hidratar en vivo si ocurre
    // MIENTRAS el modal está abierto. Patrón idéntico al de
    // checkout-page.component.ts:254 (toObservable + take(1)) — preferido
    // sobre effect() porque éste no dispara fiablemente cuando el componente
    // monta antes de que auth.me() complete en APP_INITIALIZER.
    //
    // Para aperturas posteriores con sesión ya activa, resetToProduct lee
    // currentUser() sincrónicamente y ya hidrata sin depender de esto.
    toObservable(this.currentUser)
      .pipe(
        filter((u): u is User => !!u),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        if (!this.isOpen()) return;
        this.applyUserPatchRespectingDirty(user);
      });

    // AuthModalService.close() escribe document.body.style.overflow = ''
    // aunque este modal siga abierto (ambos services tocan el body
    // directamente sin coordinación). Re-aplicamos el lock cuando detectamos
    // que AuthModal se cerró mientras nosotros seguimos abiertos.
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const preOpen = this.isOpen();
      const authOpen = this.authModalService.isOpen();
      if (preOpen && !authOpen) {
        document.body.style.overflow = 'hidden';
      }
    });

    // Cuando aparece un aviso de bloque (error del formulario `errorPanel` o
    // aviso del paso de pago `reservationNotice`) traemos el body al top, donde
    // se pintan, para que quede a la vista aunque el usuario hubiera bajado
    // hasta el CTA del fondo. role="alert" ya lo anuncia a lectores de pantalla;
    // esto cubre a usuarios videntes. Mismo scroll interno que el avance
    // form→pago (openReservation success). Al limpiarse (null) no dispara.
    effect(() => {
      const hasNotice = this.errorPanel() !== null || this.reservationNotice() !== null;
      if (!hasNotice || !isPlatformBrowser(this.platformId)) return;
      this.scrollBodyToTop();
    });

    // ESC cierra el modal en browser. Si el modal de comprobante está abierto
    // encima, ESC lo gestiona él (dismiss) y aquí no hacemos nada: ambos
    // escuchan el MISMO keydown de document y stopPropagation no frena
    // listeners hermanos del mismo nodo.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (
            e.key === 'Escape' &&
            !e.defaultPrevented &&
            this.isOpen() &&
            !this.paymentModalOpen() &&
            !this.coverageDialogOpen() &&
            !this.destinationsOpen()
          ) {
            this.onClose();
          }
        });
    });

    // Focus trap: Tab/Shift+Tab circulan solo dentro del modal. Cede el trap
    // al modal de comprobante mientras esté abierto (tiene el suyo propio).
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (
            e.key !== 'Tab' ||
            !this.isOpen() ||
            this.paymentModalOpen() ||
            this.coverageDialogOpen() ||
            this.destinationsOpen()
          ) {
            return;
          }

          const modal = this.modalRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) return;

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  protected onClose(): void {
    if (this.paymentModalSubmitting()) return;
    this.destinationsOpen.set(false);
    this.modalService.close();
  }

  protected onOpenLogin(): void {
    this.authModalService.open('login');
  }

  /**
   * El cliente aceptó el aviso de cobertura: cambiamos el método a Correos (home). El `setValue`
   * dispara `applyDeliveryMethod('home')` (recalcula envío, 30% y validadores). El paso de formulario
   * es el único donde vive el dialog (con sesión abierta se vuelve por «Volver», que la descarta).
   */
  protected onCoverageDialogAcknowledge(): void {
    this.coverageDialogOpen.set(false);
    this.form.controls.delivery_method.setValue('home');
    this.showCoverageSwitchedToast();
  }

  /**
   * Snackbar de refuerzo tras cambiar a Correos por cantón no cubierto (el cliente veía el dialog
   * como demasiado sutil). Se auto-oculta a los 10 s; «Entendido» solo lo cierra antes y «Ver destinos»
   * abre el modal de cobertura. El toast-stack (root, z-index 1100) se pinta sobre el modal (70/71).
   */
  private showCoverageSwitchedToast(): void {
    this.toastService.show({
      type: 'info',
      message: PS_DELIVERY_COPY.switchedNote,
      duration: 10000,
      actions: [
        { label: PS_DELIVERY_COPY.destinationsLink, run: () => this.destinationsOpen.set(true) },
        { label: PS_DELIVERY_COPY.switchedAck },
      ],
    });
  }

  /** Valida los inputs de texto libre solo cuando el usuario termina de editarlos. */
  protected onManualLocationBlur(): void {
    if (this.locationsLoadError()) this.reconcilePsDeliveryCoverage();
  }

  protected onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  protected increment(): void {
    const current = this.form.controls.quantity.value ?? 1;
    if (current < this.maxQuantity()) {
      this.form.controls.quantity.setValue(current + 1);
    }
  }

  protected decrement(): void {
    const current = this.form.controls.quantity.value ?? 1;
    if (current > 1) {
      this.form.controls.quantity.setValue(current - 1);
    }
  }

  protected formatCRC(amount: number): string {
    return formatCrc(amount);
  }

  /**
   * Submit del formulario: valida, guarda el payload y ABRE LA RESERVA
   * flotante (Bloque 2, D5): la sesión se abre —y el countdown de 15:00
   * arranca— al pasar al paso de pago, no al pulsar el CTA original de
   * «Reservar». La precompra se crea en `onPaymentFinalize` con el
   * comprobante adjunto (create-on-finalize, Bloque 1).
   */
  protected onSubmit(): void {
    if (this.paymentModalSubmitting() || this.sessionOpening()) return;
    const product = this.product();
    if (!product) return;
    if (this.isDeadlinePassed()) return;
    if (this.eaLocked()) return;

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.focusFirstInvalidControl();
      return;
    }

    const max = this.maxQuantity();
    const qty = this.form.controls.quantity.value ?? 1;
    if (qty > max) {
      this.form.controls.quantity.setErrors({ max: true });
      return;
    }

    const productId = Number(product.id);
    if (!Number.isFinite(productId)) return;

    const raw = this.form.getRawValue();
    const method = raw.delivery_method;
    const needsAddress = method === 'home' || method === 'ps_delivery';

    // `home` sí necesita el Delivery id=1 para calcular precio/umbral.
    // `ps_delivery` usa una tarifa por provincia y no depende de cargar el Delivery id=1.
    if (method === 'home' && !this.delivery1()) {
      this.errorPanel.set({
        reason: 'network',
        message: ERROR_WORDING.network,
      });
      this.toastService.show({ type: 'error', message: ERROR_WORDING.network });
      return;
    }

    this.pendingPayload = {
      product_id: productId,
      quantity: qty,
      name: raw.name.trim(),
      surnames: raw.surnames.trim(),
      email: raw.email.trim(),
      phone: raw.phone.trim(),
      delivery_method: method,
      delivery_price: this.deliveryPrice(),
      identification: raw.identification.trim() || null,
      province: needsAddress ? raw.province.trim() || null : null,
      canton: needsAddress ? raw.canton.trim() || null : null,
      district: needsAddress ? raw.district.trim() || null : null,
      hood: needsAddress ? raw.hood.trim() || null : null,
      directions: needsAddress ? raw.directions.trim() || null : null,
    };

    this.errorPanel.set(null);
    this.paymentModalError.set(null);
    this.openReservation('form');
  }

  /**
   * Abre (o reabre) la sesión de reserva flotante en el backend.
   *
   * `origin` decide qué hacer con la respuesta:
   *  - `'form'`   → viene de «Continuar al pago»: con 201 avanza al paso de
   *    pago; un 409 devuelve al formulario con el rótulo verbatim.
   *  - `'retry'`  → viene del CTA del aviso de expiración/cupo (ya en el paso
   *    de pago): con 201 vuelve el countdown; un 409 mantiene el aviso con el
   *    rótulo de cupo.
   *  - `'refresh'` → la reserva se invalidó al finalizar (422): reabre en
   *    silencio manteniendo el modal de comprobante (y lo adjuntado), y deja
   *    que el callback reintente una vez con el token nuevo; un 409 cierra el
   *    comprobante y muestra el aviso de cupo.
   *  - `'method'` → el usuario eligió un método distinto al congelado en la
   *    sesión: la reemplaza y solo entonces abre comprobante o navega a ONVO.
   *
   * Nadie cancela sesiones viejas desde aquí: expiran solas a los 15 min y el
   * backend expira la anterior del mismo email+producto al abrir una nueva.
   */
  private openReservation(
    origin: 'form' | 'retry' | 'refresh' | 'method',
    onRefreshed?: () => void,
    paymentMethod: PrepurchasePaymentMethod = this.selectedPaymentMethod(),
  ): void {
    const request = this.buildSessionRequest(paymentMethod);
    if (!request || this.sessionOpening()) return;

    this.sessionOpening.set(true);
    this.session.set(null);
    this.sessionPaymentMethod.set(null);

    this.prepurchaseService
      .openSession(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (session) => {
          this.sessionOpening.set(false);
          this.session.set(session);
          this.sessionPaymentMethod.set(paymentMethod);
          this.reservationNotice.set(null);
          this.errorPanel.set(null);
          if (origin === 'form') {
            this.step.set('payment');
            this.bodyRef()?.nativeElement.scrollTo({ top: 0 });
          } else if (origin === 'refresh') {
            this.paymentModalError.set(null);
            onRefreshed?.();
          } else if (origin === 'method') {
            this.continueWithPaymentMethod(paymentMethod, session);
          }
        },
        error: (err) => {
          this.sessionOpening.set(false);
          const reason: PrepurchaseErrorReason =
            err instanceof PrepurchaseError ? err.reason : 'unknown';
          const message =
            err instanceof PrepurchaseError && err.message ? err.message : ERROR_WORDING[reason];
          // Defensa en profundidad: si el 403 EA trajo un umbral, lo persistimos para
          // que el copy (aquí y en el resto de la web) deje de usar el fallback (D3).
          if (err instanceof PrepurchaseError && err.threshold !== undefined) {
            this.pointsService.recordThreshold(err.threshold);
          }

          if (origin === 'refresh' && this.paymentModalOpen()) {
            this.paymentModalSubmitting.set(false);
            this.paymentModalOpen.set(false);
            this.relockBodyScroll();
          }

          if ((reason === 'sold_out' || reason === 'too_many_requests') && origin !== 'form') {
            // Reintento sin cupo (409) o cortado por el rate-limiter (429): el
            // aviso (con el rótulo verbatim) se queda en el paso de pago y
            // conserva el CTA de reintentar. Mismo tratamiento para ambos.
            this.reservationNotice.set(reason === 'too_many_requests' ? 'throttled' : 'sold_out');
            return;
          }

          // 409 al abrir desde el form → vuelve al formulario con el rótulo
          // verbatim; 422 (límites/validación) y red → panel de error de
          // siempre. Nada de lo tecleado se pierde.
          this.step.set('form');
          this.reservationNotice.set(null);
          this.errorPanel.set({ reason, message });
        },
      });
  }

  /**
   * El countdown llegó a 0: la reserva ya no vale. Cierra el modal de
   * comprobante si está abierto y muestra el aviso de expiración verbatim con
   * el CTA de reintento. Lo tecleado en el formulario NO se pierde
   * (pendingPayload y el form siguen intactos).
   *
   * Si hay un «Finalizar» en vuelo no interrumpimos: el backend decide (puede
   * incluso crear la precompra si queda cupo derivado, Q1 del spec) y su
   * respuesta pinta éxito o error.
   */
  protected onReservationExpired(): void {
    if (this.paymentModalSubmitting() || this.status() === 'success') return;
    this.session.set(null);
    this.reservationNotice.set('expired');
    if (this.paymentModalOpen()) {
      this.paymentModalOpen.set(false);
      this.paymentModalError.set(null);
      this.relockBodyScroll();
    }
  }

  /** CTA del aviso de expiración/cupo: reabre una sesión nueva (nuevo countdown). */
  protected onRetryReservation(): void {
    this.openReservation('retry');
  }

  /**
   * El usuario eligió el método del abono:
   *  - sinpe/transfer → abre el modal de comprobante (split 30 %/70 % + copy de precompra).
   *  - online (tarjeta, Bloque 3) → sale del modal a la página de pago ONVO con el token de la reserva
   *    YA abierta. La `Prepurchase` nace al aprobarse el cobro (intent + complete/webhook), no aquí.
   */
  protected onSelectPaymentMethod(method: CheckoutPaymentMethod): void {
    const session = this.session();
    if (!this.pendingPayload || !session) return;
    this.selectedPaymentMethod.set(method);

    if (this.sessionPaymentMethod() !== method) {
      this.openReservation('method', undefined, method);
      return;
    }

    this.continueWithPaymentMethod(method, session);
  }

  /** Continúa únicamente cuando la sesión vigente fue creada para el método elegido. */
  private continueWithPaymentMethod(
    method: PrepurchasePaymentMethod,
    session: PrepurchaseSession,
  ): void {
    if (method === 'online') {
      this.modalService.close();
      this.router.navigate(['/precompra/pago/sesion', session.session_token]);
      return;
    }

    // Tras descartar 'online', method es 'sinpe' | 'transfer' (narrowing).
    this.paymentModalMethod.set(method);
    this.paymentModalError.set(null);
    this.paymentModalOpen.set(true);
  }

  /**
   * «Volver» del paso de pago: regresa al formulario sin perder lo escrito.
   * La sesión abierta NO se cancela (no hace falta: expira sola a los 15 min
   * y el backend expira la anterior al abrir una nueva del mismo
   * email+producto); solo se descarta localmente.
   */
  protected onBackToForm(): void {
    if (this.paymentModalSubmitting()) return;
    this.step.set('form');
    this.session.set(null);
    this.selectedPaymentMethod.set('sinpe');
    this.sessionPaymentMethod.set(null);
    this.reservationNotice.set(null);
    this.coverageDialogOpen.set(false);
  }

  /**
   * «Finalizar» del modal de comprobante: crea la precompra con el comprobante
   * adjunto en multipart (create-on-finalize) CONSUMIENDO la reserva flotante
   * (`session_token` viaja en el mismo multipart). Si el backend rechaza:
   *  - 409 (`sold_out`, el cupo desapareció al finalizar) → rótulo verbatim
   *    DENTRO del modal; el CTA sigue disponible para reintentar.
   *  - 422 (`invalid_session`) → se reabre una sesión nueva en silencio
   *    (nuevo token + nuevo countdown) sin cerrar el modal ni perder el
   *    comprobante adjunto, y se reintenta UNA vez automáticamente. Un segundo
   *    `invalid_session` se muestra y corta el ciclo.
   *  - Resto → error DENTRO del modal, sin cerrar ni navegar (patrón
   *    `onPaymentFinalize` de checkout-page).
   */
  protected onPaymentFinalize(file: File): void {
    if (!this.pendingPayload || !this.session()?.session_token || this.paymentModalSubmitting()) {
      return;
    }

    this.paymentModalSubmitting.set(true);
    this.paymentModalError.set(null);
    this.submitPaymentProof(file, true);
  }

  /**
   * Envía el comprobante con la sesión vigente. `allowSessionRefresh` limita a
   * UNO el reintento automático: evita bucles si el backend vuelve a rechazar
   * también el token recién emitido.
   */
  private submitPaymentProof(file: File, allowSessionRefresh: boolean): void {
    const pending = this.pendingPayload;
    const token = this.session()?.session_token;
    if (!pending || !token) {
      this.paymentModalSubmitting.set(false);
      return;
    }

    const payload: PrepurchaseRequest = {
      ...pending,
      payment_method: this.paymentModalMethod(),
      session_token: token,
    };

    this.prepurchaseService
      .createWebPrepurchase(payload, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saveContactCache(payload);
          this.paymentModalSubmitting.set(false);
          this.paymentModalOpen.set(false);
          this.pendingPayload = null;
          this.status.set('success');
          this.relockBodyScroll();
          // Confirmación de pago (SINPE/transferencia): refresca el saldo PP
          // solo si hay sesión (D14: la precompra de invitado no acumula).
          if (this.isAuthenticated()) {
            this.pointsService.refresh();
          }
        },
        error: (err) => {
          const reason: PrepurchaseErrorReason =
            err instanceof PrepurchaseError ? err.reason : 'unknown';
          const message =
            err instanceof PrepurchaseError && err.message ? err.message : ERROR_WORDING[reason];
          if (err instanceof PrepurchaseError && err.threshold !== undefined) {
            this.pointsService.recordThreshold(err.threshold);
          }
          if (reason === 'invalid_session' && allowSessionRefresh) {
            this.paymentModalError.set(null);
            this.openReservation('refresh', () => this.submitPaymentProof(file, false));
            return;
          }

          this.paymentModalSubmitting.set(false);
          this.paymentModalError.set(message);
        },
      });
  }

  /** «Cancelar» / Escape del modal de comprobante: vuelve al paso de método sin crear nada. */
  protected onPaymentDismiss(): void {
    if (this.paymentModalSubmitting()) return;
    this.paymentModalOpen.set(false);
    this.paymentModalError.set(null);
    this.relockBodyScroll();
  }

  /**
   * El modal de comprobante libera `body.overflow` al cerrarse aunque este
   * modal siga abierto (mismo descoordino que con AuthModal, ver effect del
   * constructor). El microtask re-aplica el lock DESPUÉS de que su effect
   * haya corrido.
   */
  private relockBodyScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    queueMicrotask(() => {
      if (this.isOpen()) document.body.style.overflow = 'hidden';
    });
  }

  protected onViewOtherPrepurchases(): void {
    this.modalService.close();
    this.router.navigate(['/tienda/precompras']);
  }

  protected hasError(field: keyof typeof this.form.controls): boolean {
    const ctrl = this.form.controls[field];
    return ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  protected errorFor(field: keyof typeof this.form.controls): string {
    const ctrl = this.form.controls[field];
    if (!ctrl.errors) return '';
    if (ctrl.errors['required']) return 'Campo requerido';
    if (ctrl.errors['email']) return 'Correo no válido';
    if (ctrl.errors['maxlength']) return 'Demasiado largo';
    if (ctrl.errors['min']) return 'Cantidad inválida';
    if (ctrl.errors['max']) return `Máximo ${this.maxQuantity()} unidades`;
    return 'Valor no válido';
  }

  protected errorHeading(): string {
    const panel = this.errorPanel();
    if (!panel) return '';
    // El copy de Early Access ya viene interpolado (umbral/saldo reales) en
    // `panel.message` — mostrar la constante estática de `ERROR_WORDING` aquí
    // duplicaría el texto (encabezado genérico + detalle específico).
    if (panel.reason === 'ea_login_required' || panel.reason === 'ea_insufficient_points') {
      return panel.message;
    }
    return ERROR_WORDING[panel.reason] || panel.message;
  }

  protected onImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && !img.src.endsWith(FALLBACK_IMAGE_URL)) {
      img.src = FALLBACK_IMAGE_URL;
    }
  }

  // ── Privates ──────────────────────────────────────────────────────────

  /** Lleva el body scrolleable del modal al inicio (donde se pinta el aviso). */
  private scrollBodyToTop(): void {
    this.bodyRef()?.nativeElement.scrollTo({ top: 0, behavior: scrollBehavior() });
  }

  /**
   * Scroll + foco al primer control inválido del formulario cuando el submit
   * falla la validación (mismo patrón que checkout-page.focusFirstInvalidControl,
   * acotado al body del modal). Sin esto, con el scroll bajado en el CTA del
   * fondo, el error de campo aparece fuera de vista.
   */
  private focusFirstInvalidControl(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const first = this.bodyRef()?.nativeElement.querySelector<HTMLElement>(
      '.pre-modal__form input.ng-invalid, .pre-modal__form select.ng-invalid, .pre-modal__form textarea.ng-invalid',
    );
    if (!first) return;
    first.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
    // El setTimeout evita que el browser cancele el smooth-scroll al enfocar
    // inmediatamente (misma razón que en checkout-page).
    setTimeout(() => first.focus({ preventScroll: true }), 100);
  }

  /**
   * Payload de `POST /prepurchase-sessions` a partir del form ya validado.
   * El API exige que `payment_method` coincida al consumirla. La primera reserva
   * arranca con SINPE para iniciar el timer en D5; si el usuario elige transferencia
   * o tarjeta, `openReservation('method')` la reemplaza antes de continuar.
   */
  private buildSessionRequest(
    paymentMethod: PrepurchasePaymentMethod,
  ): PrepurchaseSessionRequest | null {
    const pending = this.pendingPayload;
    if (!pending) return null;
    return {
      product_id: pending.product_id,
      quantity: pending.quantity,
      email: pending.email,
      phone: pending.phone,
      delivery_price: pending.delivery_price,
      payment_method: paymentMethod,
      identification: pending.identification,
      name: pending.name,
      surnames: pending.surnames,
      province: pending.province,
      canton: pending.canton,
      district: pending.district,
      hood: pending.hood,
      directions: pending.directions,
      delivery_method: pending.delivery_method,
    };
  }

  private resetToProduct(product: Product): void {
    const cached = this.loadContactCache();
    const fromUser = this.buildPatchFromUser(this.currentUser());
    // Usuario logueado gana sobre cache; cache gana sobre vacío. El form.reset
    // limpia `dirty` — la prioridad solo aplica al instante de apertura; la
    // edición manual posterior queda protegida por los checks dirty en
    // applyUserPatchRespectingDirty.
    const merged: CachedContact = { ...cached, ...fromUser };
    const initialQty = Math.min(this.modalService.initialQty(), this.computeMaxFor(product));

    this.form.enable({ emitEvent: false });
    this.form.reset(
      {
        quantity: initialQty,
        name: merged.name ?? '',
        surnames: merged.surnames ?? '',
        identification: merged.identification ?? '',
        email: merged.email ?? '',
        phone: merged.phone ?? '',
        delivery_method: 'ps',
        province: merged.province ?? '',
        canton: merged.canton ?? '',
        district: merged.district ?? '',
        hood: merged.hood ?? '',
        directions: merged.directions ?? '',
      },
      { emitEvent: false },
    );
    // form.reset con emitEvent:false no dispara la suscripción a
    // valueChanges, así que reaplicamos los validators y sincronizamos el
    // signal manualmente. resetAddress:false preserva los datos recién
    // hidratados desde cache/usuario.
    this.applyDeliveryMethod('ps', { resetAddress: false });
    this.status.set('idle');
    this.errorPanel.set(null);
    this.step.set('form');
    this.pendingPayload = null;
    this.paymentModalOpen.set(false);
    this.paymentModalSubmitting.set(false);
    this.paymentModalError.set(null);
    // Reserva flotante: descartar la sesión local (si existía, expira sola).
    this.session.set(null);
    this.selectedPaymentMethod.set('sinpe');
    this.sessionPaymentMethod.set(null);
    this.sessionOpening.set(false);
    this.reservationNotice.set(null);
    this.coverageDialogOpen.set(false);
    this.destinationsOpen.set(false);
  }

  private applyDeliveryMethod(
    method: PrepurchaseDeliveryMethod,
    options: { resetAddress: boolean },
  ): void {
    this.deliveryMethod.set(method);
    const needs = method === 'home' || method === 'ps_delivery';
    const addressCtrls = ['province', 'canton', 'district', 'directions'] as const;
    addressCtrls.forEach((key) => {
      const ctrl = this.form.controls[key];
      const maxLen = key === 'directions' ? 300 : 100;
      const base = [Validators.maxLength(maxLen)];
      ctrl.setValidators(needs ? [...base, Validators.required] : base);
      ctrl.updateValueAndValidity({ emitEvent: false });
    });
    if (!needs && options.resetAddress) {
      this.form.patchValue(
        { province: '', canton: '', district: '', hood: '', directions: '' },
        { emitEvent: false },
      );
    }

    // Sincroniza los espejos tras el cambio silencioso: solo reflejan provincia/cantón cuando el
    // método usa dirección (así ps_delivery no queda deshabilitado por una dirección oculta en cache).
    this.provinceValue.set(needs ? (this.form.controls.province.value ?? '') : '');
    this.cantonValue.set(needs ? (this.form.controls.canton.value ?? '') : '');
  }

  private buildPatchFromUser(user: User | null): CachedContact {
    if (!user) return {};
    const c = user.client;
    return {
      email: user.email || undefined,
      name: c?.name ?? user.name ?? undefined,
      surnames: c?.surnames ?? undefined,
      identification: c?.identification ?? undefined,
      phone: c?.phone ?? undefined,
      province: c?.province ?? undefined,
      canton: c?.canton ?? undefined,
      district: c?.district ?? undefined,
      hood: c?.hood ?? undefined,
      directions: c?.directions ?? undefined,
    };
  }

  private applyUserPatchRespectingDirty(user: User): void {
    const c = user.client;
    const controls = this.form.controls;
    type StringKey = Exclude<keyof typeof controls, 'quantity' | 'delivery_method'>;
    const patch: Partial<Record<StringKey, string>> = {};

    const assignIf = (key: StringKey, value: string | null | undefined) => {
      if (!value) return;
      if (controls[key].dirty) return;
      patch[key] = value;
    };

    assignIf('email', user.email);
    assignIf('name', c?.name ?? user.name);
    assignIf('surnames', c?.surnames);
    assignIf('identification', c?.identification);
    assignIf('phone', c?.phone);
    assignIf('province', c?.province);
    assignIf('canton', c?.canton);
    assignIf('district', c?.district);
    assignIf('hood', c?.hood);
    assignIf('directions', c?.directions);

    if (Object.keys(patch).length > 0) {
      this.form.patchValue(patch, { emitEvent: false });
      this.syncLocationSignals();
      this.reconcilePsDeliveryCoverage();
    }
  }

  /** Sincroniza los signals de ubicación tras un patch silencioso del formulario. */
  private syncLocationSignals(): void {
    const needsAddress =
      this.deliveryMethod() === 'home' || this.deliveryMethod() === 'ps_delivery';
    this.provinceValue.set(needsAddress ? (this.form.controls.province.value ?? '') : '');
    this.cantonValue.set(needsAddress ? (this.form.controls.canton.value ?? '') : '');
  }

  /** Abre el aviso cuando el método activo y la dirección completa quedan fuera de cobertura. */
  private reconcilePsDeliveryCoverage(): void {
    if (this.deliveryMethod() !== 'ps_delivery') return;
    const province = this.form.controls.province.value;
    const canton = this.form.controls.canton.value;
    if (province && canton && !isPsDeliveryCovered(province, canton)) {
      this.coverageDialogOpen.set(true);
    }
  }

  private computeMaxFor(product: Product): number {
    const stock = product.prepurchase_stock ?? 0;
    const perClient = product.prepurchase_limit ?? 0;
    if (perClient > 0) return Math.max(1, Math.min(stock, perClient));
    return Math.max(1, stock);
  }

  private loadContactCache(): CachedContact {
    if (!isPlatformBrowser(this.platformId)) return {};
    try {
      const raw = localStorage.getItem(CONTACT_CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as CachedContact;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private saveContactCache(payload: PrepurchaseRequest): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const cached: CachedContact = {
      name: payload.name,
      surnames: payload.surnames,
      identification: payload.identification ?? undefined,
      email: payload.email,
      phone: payload.phone,
      province: payload.province ?? undefined,
      canton: payload.canton ?? undefined,
      district: payload.district ?? undefined,
      hood: payload.hood ?? undefined,
      directions: payload.directions ?? undefined,
    };
    try {
      localStorage.setItem(CONTACT_CACHE_KEY, JSON.stringify(cached));
    } catch {
      // localStorage puede estar lleno o deshabilitado (modo privado). Ignoramos.
    }
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null,
    );
  }
}
